import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Shield, CheckCircle2, Clock, AlertTriangle, Loader2, ShieldCheck,
  RotateCcw, PenSquare, X, ShieldOff, Inbox, Building2, Users,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ADMIN_NAV = [
  { icon: Shield, label: 'Compliance', href: '/admin/compliance' },
  { icon: ShieldCheck, label: 'AI Validation', href: '/admin/ai-validation', active: true },
  { icon: Inbox, label: 'Intake Inbox', href: '/admin/intake' },
  { icon: Building2, label: 'Referral Sources', href: '/admin/referral-sources' },
  { icon: Users, label: 'CRM', href: '/admin/crm' },
];

const STATE_BADGE = {
  signed: {
    label: 'Signed',
    class: 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30',
    icon: CheckCircle2,
  },
  refresh_due: {
    label: 'Refresh Due',
    class: 'bg-amber-500/15 text-amber-300 border border-amber-500/30',
    icon: Clock,
  },
  pending: {
    label: 'Pending',
    class: 'bg-rose-500/15 text-rose-300 border border-rose-500/30',
    icon: AlertTriangle,
  },
};

const RISK_TINT = {
  high: 'text-rose-300',
  medium: 'text-amber-300',
  low: 'text-emerald-300',
};

export default function AdminAiValidation() {
  const { getAuthHeader } = useAuth();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSurface, setActiveSurface] = useState(null);
  // Row-level revoke — confirmation dialog state so we don't lean on
  // window.prompt() (blocked by Playwright + jarring UX). Same modal is
  // reused for both the row-level "Revoke" button and any future
  // programmatic revokes.
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revokeReason, setRevokeReason] = useState('');
  const [revokeBusy, setRevokeBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/admin/ai/validation`,
        getAuthHeader(),
      );
      setRows(data.rows || []);
      setSummary(data.summary || null);
    } catch (e) {
      toast.error('Failed to load AI validation console');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const submitRevoke = async () => {
    if (!revokeTarget) return;
    if (revokeReason.trim().length < 2) {
      toast.error('Please provide a reason (2+ characters)');
      return;
    }
    setRevokeBusy(true);
    try {
      await axios.post(
        `${API}/admin/ai/validation/${encodeURIComponent(revokeTarget.surface_id)}/revoke`,
        { reason: revokeReason.trim() },
        getAuthHeader(),
      );
      toast.success(`Revoked sign-off · ${revokeTarget.name}`);
      setRevokeTarget(null);
      setRevokeReason('');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Revoke failed');
    } finally {
      setRevokeBusy(false);
    }
  };

  return (
    <DashboardShell navItems={ADMIN_NAV}>
      <div className="p-6 md:p-10 space-y-6" data-testid="admin-ai-validation-page">
        <header className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">
              Clinical Validation Console
            </h1>
            <p className="text-sm text-slate-400 mt-1 max-w-2xl">
              Every AI surface, its clinical-validation posture, and the reviewer
              accountable for it. Sign-offs are captured with reviewer NPI, cases
              reviewed, and pass rate — buyers see the same signal in the
              procurement envelope.
            </p>
          </div>
          <Button
            onClick={load}
            variant="outline"
            size="sm"
            data-testid="reload-btn"
          >
            <RotateCcw className="h-4 w-4 mr-2" /> Reload
          </Button>
        </header>

        {summary && (
          <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <SummaryCard label="Total surfaces" value={summary.total} testId="summary-total" />
            <SummaryCard label="Signed" value={summary.signed} tone="emerald" testId="summary-signed" />
            <SummaryCard label="Refresh due" value={summary.refresh_due} tone="amber" testId="summary-refresh" />
            <SummaryCard label="Pending" value={summary.pending} tone="rose" testId="summary-pending" />
          </section>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-24 text-slate-400">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : (
          <div className="rounded-xl border border-slate-800/80 overflow-hidden bg-slate-900/40">
            <table className="w-full text-sm" data-testid="ai-validation-table">
              <thead className="bg-slate-900/70 text-slate-400 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Surface</th>
                  <th className="text-left px-4 py-3">Risk</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Reviewer</th>
                  <th className="text-left px-4 py-3">Pass rate</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const badge = STATE_BADGE[r.state] || STATE_BADGE.pending;
                  const Icon = badge.icon;
                  const v = r.validation || {};
                  return (
                    <tr
                      key={r.surface_id}
                      className="border-t border-slate-800/70 hover:bg-slate-900/60"
                      data-testid={`row-${r.surface_id}`}
                    >
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-100">{r.name}</div>
                        <div className="text-xs text-slate-500 font-mono">
                          {r.surface_id}
                        </div>
                      </td>
                      <td className={`px-4 py-3 font-medium ${RISK_TINT[r.risk_class] || 'text-slate-300'}`}>
                        {r.risk_class}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs ${badge.class}`}>
                          <Icon className="h-3.5 w-3.5" />
                          {badge.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-300">
                        {v.reviewer_name ? (
                          <>
                            <div>{v.reviewer_name}</div>
                            {v.npi && (
                              <div className="text-xs text-slate-500 font-mono">NPI {v.npi}</div>
                            )}
                          </>
                        ) : (
                          <span className="text-slate-500">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-300">
                        {typeof v.pass_rate === 'number'
                          ? `${Math.round(v.pass_rate * 100)}%`
                          : <span className="text-slate-500">—</span>}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setActiveSurface(r)}
                            data-testid={`signoff-btn-${r.surface_id}`}
                          >
                            <PenSquare className="h-3.5 w-3.5 mr-1.5" />
                            {r.state === 'signed' ? 'Re-sign' : 'Sign off'}
                          </Button>
                          {r.state === 'signed' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => { setRevokeReason(''); setRevokeTarget(r); }}
                              className="text-rose-300 border-rose-500/40 hover:bg-rose-500/10"
                              data-testid={`revoke-btn-${r.surface_id}`}
                              title="Revoke this sign-off"
                            >
                              <ShieldOff className="h-3.5 w-3.5 mr-1.5" />
                              Revoke
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {activeSurface && (
        <SignoffModal
          surface={activeSurface}
          onClose={() => setActiveSurface(null)}
          onDone={() => { setActiveSurface(null); load(); }}
          getAuthHeader={getAuthHeader}
        />
      )}

      {revokeTarget && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[70] flex items-center justify-center p-4"
          data-testid="revoke-modal"
          onClick={() => !revokeBusy && setRevokeTarget(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-slate-950 border border-rose-500/30 rounded-2xl w-full max-w-md shadow-2xl"
          >
            <header className="p-5 border-b border-slate-800 flex items-start gap-3">
              <ShieldOff className="h-6 w-6 text-rose-400 flex-shrink-0 mt-0.5" />
              <div>
                <h2 className="text-lg font-semibold">Revoke sign-off</h2>
                <p className="text-xs text-slate-500 font-mono mt-0.5">
                  {revokeTarget.surface_id}
                </p>
              </div>
            </header>
            <div className="p-5 space-y-3">
              <p className="text-sm text-slate-300">
                Revoking <span className="font-medium text-slate-100">{revokeTarget.name}</span>
                {' '}will immediately return the surface to <span className="text-rose-300">pending</span>.
                If this surface is HIGH-risk it will be structurally
                disabled in production until a new sign-off is captured.
              </p>
              <label className="block">
                <span className="text-xs text-slate-400 uppercase tracking-wide">
                  Reason for revoke *
                </span>
                <textarea
                  rows={3}
                  autoFocus
                  value={revokeReason}
                  onChange={(e) => setRevokeReason(e.target.value)}
                  className="mt-1 w-full bg-slate-900 border border-slate-800 rounded-lg px-3 py-2 text-sm"
                  placeholder="e.g. clinical drift observed in QA sample; new evaluation required"
                  data-testid="revoke-reason-input"
                />
              </label>
            </div>
            <footer className="p-5 border-t border-slate-800 flex items-center justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setRevokeTarget(null)}
                disabled={revokeBusy}
                data-testid="revoke-cancel"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={submitRevoke}
                disabled={revokeBusy || revokeReason.trim().length < 2}
                className="bg-rose-500/90 hover:bg-rose-500 text-white"
                data-testid="revoke-confirm"
              >
                {revokeBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirm revoke'}
              </Button>
            </footer>
          </div>
        </div>
      )}
    </DashboardShell>
  );
}

function SummaryCard({ label, value, tone = 'slate', testId }) {
  const tones = {
    slate: 'text-slate-200',
    emerald: 'text-emerald-300',
    amber: 'text-amber-300',
    rose: 'text-rose-300',
  };
  return (
    <div className="rounded-xl bg-slate-900/60 border border-slate-800/70 p-4" data-testid={testId}>
      <div className="text-xs text-slate-400 uppercase tracking-wide">{label}</div>
      <div className={`mt-1 text-2xl font-semibold ${tones[tone]}`}>{value}</div>
    </div>
  );
}

function SignoffModal({ surface, onClose, onDone, getAuthHeader }) {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    reviewer_name: surface.validation?.reviewer_name || '',
    credentials: (surface.validation?.credentials || []).join(', '),
    npi: surface.validation?.npi || '',
    cases_reviewed: surface.validation?.cases_reviewed || 50,
    pass_rate: (surface.validation?.pass_rate ?? 0.95),
    expires_at: surface.validation?.expires_at || '',
    notes: surface.validation?.notes || '',
  });

  const submit = async (e) => {
    e.preventDefault();
    if (form.npi && !/^\d{10}$/.test(form.npi)) {
      toast.error('NPI must be exactly 10 digits');
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/ai/validation/${encodeURIComponent(surface.surface_id)}/signoff`,
        {
          reviewer_name: form.reviewer_name.trim(),
          credentials: form.credentials.split(',').map(c => c.trim()).filter(Boolean),
          npi: form.npi.trim() || null,
          cases_reviewed: Number(form.cases_reviewed),
          pass_rate: Number(form.pass_rate),
          expires_at: form.expires_at || null,
          notes: form.notes.trim() || null,
        },
        getAuthHeader(),
      );
      toast.success('Sign-off saved');
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Sign-off failed');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    const reason = window.prompt('Reason for revoking sign-off?');
    if (!reason || reason.trim().length < 2) return;
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/ai/validation/${encodeURIComponent(surface.surface_id)}/revoke`,
        { reason: reason.trim() },
        getAuthHeader(),
      );
      toast.success('Sign-off revoked');
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Revoke failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      data-testid="signoff-modal"
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-slate-950 border border-slate-800 rounded-2xl w-full max-w-2xl shadow-2xl"
      >
        <header className="p-6 border-b border-slate-800 flex items-start justify-between">
          <div>
            <h2 className="text-xl font-semibold">Sign off · {surface.name}</h2>
            <p className="text-xs text-slate-500 font-mono mt-1">{surface.surface_id}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-200"
            data-testid="signoff-close"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="p-6 space-y-4">
          <Field label="Reviewer name *">
            <input
              required
              value={form.reviewer_name}
              onChange={(e) => setForm({ ...form, reviewer_name: e.target.value })}
              className="w-full bg-slate-900 border border-slate-800 rounded-lg px-3 py-2 text-sm"
              data-testid="signoff-reviewer-name"
            />
          </Field>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Credentials (comma-separated)">
              <input
                placeholder="MD, FACC"
                value={form.credentials}
                onChange={(e) => setForm({ ...form, credentials: e.target.value })}
                className="w-full bg-slate-900 border border-slate-800 rounded-lg px-3 py-2 text-sm"
                data-testid="signoff-credentials"
              />
            </Field>
            <Field label="NPI (10 digits)">
              <input
                pattern="\d{10}"
                placeholder="1234567890"
                value={form.npi}
                onChange={(e) => setForm({ ...form, npi: e.target.value })}
                className="w-full bg-slate-900 border border-slate-800 rounded-lg px-3 py-2 text-sm font-mono"
                data-testid="signoff-npi"
              />
            </Field>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Cases reviewed *">
              <input
                required
                type="number"
                min="1"
                value={form.cases_reviewed}
                onChange={(e) => setForm({ ...form, cases_reviewed: e.target.value })}
                className="w-full bg-slate-900 border border-slate-800 rounded-lg px-3 py-2 text-sm"
                data-testid="signoff-cases"
              />
            </Field>
            <Field label={`Pass rate * (${Math.round(form.pass_rate * 100)}%)`}>
              <input
                required
                type="range"
                min="0" max="1" step="0.01"
                value={form.pass_rate}
                onChange={(e) => setForm({ ...form, pass_rate: e.target.value })}
                className="w-full"
                data-testid="signoff-passrate"
              />
            </Field>
          </div>

          <Field label="Expires at (optional YYYY-MM-DD)">
            <input
              type="date"
              value={form.expires_at}
              onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
              className="w-full bg-slate-900 border border-slate-800 rounded-lg px-3 py-2 text-sm"
              data-testid="signoff-expires"
            />
          </Field>

          <Field label="Notes">
            <textarea
              rows={3}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="w-full bg-slate-900 border border-slate-800 rounded-lg px-3 py-2 text-sm"
              placeholder="Methodology, dataset id, references..."
              data-testid="signoff-notes"
            />
          </Field>
        </div>

        <footer className="p-6 border-t border-slate-800 flex items-center justify-between">
          {surface.state === 'signed' ? (
            <Button
              type="button"
              variant="outline"
              onClick={revoke}
              disabled={busy}
              className="text-rose-300 border-rose-500/40"
              data-testid="signoff-revoke"
            >
              Revoke
            </Button>
          ) : <span />}
          <div className="flex gap-3">
            <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy} data-testid="signoff-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save sign-off'}
            </Button>
          </div>
        </footer>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs text-slate-400 uppercase tracking-wide">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
